import { useState, type FormEvent, type ReactElement } from 'react';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { v2, ApiError, type UnlockKind } from '@/api/v2Client';

const KIND_LABEL: Record<UnlockKind, string> = {
  edit: 'Unlock to edit — content changes permitted',
  renumber: 'Unlock to renumber — sequence renumbering permitted',
};

interface UnlockDialogProps {
  readonly submissionId: string;
  /** 'grant' opens a NEW unlock; 'relock' closes an existing one. Both require a reason (ratified design). */
  readonly mode: 'grant' | 'relock';
  /** For relock, pre-select the kind being closed (the caller already knows which is active). */
  readonly initialKind?: UnlockKind;
  readonly onClose: () => void;
  readonly onDone: () => void;
}

/**
 * phase-admin-unlock — the mandatory-reason dialog for both directions of
 * the ratified admin-unlock capability. Unlocking WITHOUT a reason is
 * refused server-side (400); this dialog makes that refusal unreachable in
 * the normal flow by disabling Submit until a reason is typed.
 */
export function UnlockDialog({
  submissionId,
  mode,
  initialKind,
  onClose,
  onDone,
}: UnlockDialogProps): ReactElement {
  const [kind, setKind] = useState<UnlockKind>(initialKind ?? 'edit');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (trimmed === '') {
      setError('A reason is required — describe why this sequence is being unlocked.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === 'grant') {
        await v2.submissions.grantUnlock(submissionId, { kind, reason: trimmed });
      } else {
        await v2.submissions.relockGrant(submissionId, { kind, reason: trimmed });
      }
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.message || 'Request failed')
          : 'Something went wrong — please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'grant' ? 'Unlock sequence' : 'Relock sequence';

  return (
    <MovableDialog
      persistKey="submissionDetail.unlockDialog"
      title={title}
      ariaLabel={title}
      onClose={onClose}
      closeOnBackdrop
      width="min(520px, 92vw)"
      zIndex={310}
    >
      <form onSubmit={(e) => void handleSubmit(e)}>
        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg)',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {mode === 'grant' && (
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            <span style={{ display: 'block', marginBottom: 4 }}>Kind *</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as UnlockKind)}
              aria-label="Unlock kind"
              data-testid="unlock-kind-select"
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}
            >
              <option value="edit">{KIND_LABEL.edit}</option>
              <option value="renumber">{KIND_LABEL.renumber}</option>
            </select>
          </label>
        )}

        <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          <span style={{ display: 'block', marginBottom: 4 }}>
            {mode === 'grant' ? 'Reason for unlocking *' : 'Reason for relocking *'}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            aria-label={mode === 'grant' ? 'Reason for unlocking' : 'Reason for relocking'}
            data-testid="unlock-reason-input"
            placeholder="Describe why this sequence is being unlocked — recorded verbatim in the audit trail."
            rows={4}
            style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, resize: 'vertical' }}
          />
        </label>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="unlock-dialog-submit"
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : mode === 'grant' ? 'Unlock' : 'Relock'}
          </button>
        </div>
      </form>
    </MovableDialog>
  );
}
